import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserRole, isAdminRole } from '@lombok/shared';
import type {
  AdjustStockInput,
  AllocatePaymentInput,
  ConvertQuoteInput,
  CreateDistributorInput,
  CreateInvoiceFromOrderInput,
  CreateOrderInput,
  CreatePaymentInput,
  CreateProductInput,
  CreateCustomerInput,
  CreateQuoteInput,
  CreateSkuInput,
  ListQuery,
  MarkNotificationsReadInput,
  OrderDecisionInput,
  PaymentDecisionInput,
  ConfirmDocumentInput,
  CreateBrandInput,
  CreateCategoryInput,
  CreatePromotionInput,
  QuoteDecisionInput,
  ReviewDocumentInput,
  SetExchangeRateInput,
  UpdateCustomerInput,
  AccountStatusActionInput,
  UpdateBrandInput,
  UpdateCategoryInput,
  UpdatePromotionInput,
  UpdateDistributorInput,
  UpdateProductInput,
} from '@lombok/shared';
import type {
  InventoryQuery,
  InvoicesQuery,
  NotificationsQuery,
  OrdersQuery,
  PaymentsQuery,
  QuotesQuery,
} from '@lombok/api-client';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryClient';
import { ALL_PAGES_SIZE, fetchAllPages } from './pagination';
import { useAuth } from '@/auth/AuthContext';

/**
 * Thin data layer: every server interaction is a typed hook over @lombok/api-client.
 * Components never call fetch or the client directly — they use these hooks so
 * caching, retries, and error typing (ApiError) are consistent everywhere.
 */

/** API readiness probe (used by the dashboard status indicator). */
export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => api.health(),
    staleTime: 60_000,
  });
}

/**
 * The authenticated user's profile from `GET /me` — the server-verified source
 * of truth (the JWT claims are only the bootstrap shown until this resolves).
 * Gate with `enabled` so it never fires while signed out.
 */
export function useMe(enabled = true) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.me(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Catalog/product list (server-side role scoping applies). */
export function useProducts(query?: ListQuery & { scope?: 'active' | 'all' }) {
  return useQuery({
    queryKey: queryKeys.products(query),
    queryFn: () => api.listProducts(query),
  });
}

/**
 * Product list for the Productos screen: EVERY page, so rows hidden on screen (archived ones)
 * can never use up a page and push older active products out. Admin also gets inactive and
 * archived products; everyone else only the active catalog.
 */
export function useProductList(admin: boolean) {
  return useQuery({
    queryKey: queryKeys.products({ list: admin ? 'admin-all' : 'active' }),
    queryFn: () =>
      fetchAllPages((cursor) =>
        api.listProducts({ limit: ALL_PAGES_SIZE, cursor, ...(admin ? { scope: 'all' as const } : {}) }),
      ),
  });
}

/** Brands / categories for the product form selectors (a handful of rows, rarely change). */
export function useBrands() {
  return useQuery({ queryKey: queryKeys.brands, queryFn: () => api.listBrands() });
}

export function useCategories() {
  return useQuery({ queryKey: queryKeys.categories, queryFn: () => api.listCategories() });
}

/** Delete (admin). Each refreshes the lists it appears in; the API refuses when there is history. */
export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Producto eliminado' },
    mutationFn: (id: string) => api.deleteProduct(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useDeletePromotion() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Promoción eliminada' },
    mutationFn: (id: string) => api.deletePromotion(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.promotions }),
  });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Marca eliminada' },
    mutationFn: (id: string) => api.deleteBrand(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.brands }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Categoría eliminada' },
    mutationFn: (id: string) => api.deleteCategory(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.categories }),
  });
}

/** Brand / category management (admin). Each change refreshes the list and the product selectors. */
export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Marca creada' },
    mutationFn: (input: CreateBrandInput) => api.createBrand(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.brands }),
  });
}

export function useUpdateBrand() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Marca actualizada' },
    mutationFn: ({ id, input }: { id: string; input: UpdateBrandInput }) => api.updateBrand(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.brands }),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Categoría creada' },
    mutationFn: (input: CreateCategoryInput) => api.createCategory(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.categories }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Categoría actualizada' },
    mutationFn: ({ id, input }: { id: string; input: UpdateCategoryInput }) => api.updateCategory(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.categories }),
  });
}

/**
 * Whole active catalog for the order/quote pickers: follows `nextCursor` page by page.
 * Returns the same `{ items }` shape as {@link useProducts}.
 */
export function useProductCatalog() {
  return useQuery({
    queryKey: queryKeys.products({ catalog: 'all' }),
    queryFn: () => fetchAllPages((cursor) => api.listProducts({ limit: ALL_PAGES_SIZE, cursor })),
  });
}

/** Customer list (server-side role scoping applies). */
export function useCustomers(query?: ListQuery) {
  return useQuery({
    queryKey: queryKeys.customers(query),
    queryFn: () => api.listCustomers(query),
  });
}

/** A single customer (account, credit, balance). */
export function useCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customer(customerId ?? ''),
    queryFn: () => api.getCustomer(customerId as string),
    enabled: Boolean(customerId),
  });
}

/** Create a customer; refreshes the customers list on success. */
export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Cliente creado' },
    mutationFn: (input: CreateCustomerInput) => api.createCustomer(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/** Update a customer; refreshes both the detail and the list. */
export function useUpdateCustomer(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Cliente actualizado' },
    mutationFn: (input: UpdateCustomerInput) => api.updateCustomer(customerId, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.customer(customerId), updated);
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/** Approve / reject / hold / suspend / reactivate / reopen a customer (admin). */
export function useChangeCustomerStatus(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Estado del cliente actualizado' },
    mutationFn: (input: AccountStatusActionInput) => api.changeCustomerStatus(customerId, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.customer(customerId), updated);
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/** A single catalog product. */
export function useProduct(productId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.product(productId ?? ''),
    queryFn: () => api.getProduct(productId as string),
    enabled: Boolean(productId),
  });
}

/** Create a product; refreshes the catalog list on success. */
export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Producto creado' },
    mutationFn: (input: CreateProductInput) => api.createProduct(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/** Update a product; refreshes both the detail and the list. */
export function useUpdateProduct(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Producto actualizado' },
    mutationFn: (input: UpdateProductInput) => api.updateProduct(productId, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.product(productId), updated);
      void qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/** Distributor list (admin). */
export function useDistributors(query?: ListQuery) {
  return useQuery({
    queryKey: queryKeys.distributors(query),
    queryFn: () => api.listDistributors(query),
  });
}

/** A single distributor (territories, locations). */
export function useDistributor(distributorId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.distributor(distributorId ?? ''),
    queryFn: () => api.getDistributor(distributorId as string),
    enabled: Boolean(distributorId),
  });
}

/** Create a distributor; refreshes the distributors list on success. */
export function useCreateDistributor() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Distribuidor creado' },
    mutationFn: (input: CreateDistributorInput) => api.createDistributor(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['distributors'] });
    },
  });
}

/** Update a distributor; refreshes both the detail and the list. */
export function useUpdateDistributor(distributorId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Distribuidor actualizado' },
    mutationFn: (input: UpdateDistributorInput) => api.updateDistributor(distributorId, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.distributor(distributorId), updated);
      void qc.invalidateQueries({ queryKey: ['distributors'] });
    },
  });
}

/** Same as useChangeCustomerStatus, for a distributor. */
export function useChangeDistributorStatus(distributorId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Estado del distribuidor actualizado' },
    mutationFn: (input: AccountStatusActionInput) => api.changeDistributorStatus(distributorId, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.distributor(distributorId), updated);
      void qc.invalidateQueries({ queryKey: ['distributors'] });
    },
  });
}

/**
 * Name and RIF of every customer and distributor by account id, for screens that list records
 * keyed by account (document review, invoices, quotes, reports). Reads every page of both
 * lists (distributors only for admin/finance: a sales rep would get a 403, so it is not asked) and gets
 * what it can read; callers fall back to the id for anything missing.
 */
export function useAccountNames(enabled = true) {
  const { session } = useAuth();
  // Only admin and finance may list distributors (the API answers 403 to the rest).
  const withDistributors = session ? isAdminRole(session.user.role) || session.user.role === UserRole.FINANCE : false;
  return useQuery({
    queryKey: ['account-names', withDistributors] as const,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const none = { items: [] as never[] };
      const [customers, distributors] = await Promise.all([
        fetchAllPages((cursor) => api.listCustomers({ limit: ALL_PAGES_SIZE, cursor })).catch(() => none),
        withDistributors
          ? fetchAllPages((cursor) => api.listDistributors({ limit: ALL_PAGES_SIZE, cursor })).catch(() => none)
          : Promise.resolve(none),
      ]);
      const names = new Map<string, { name: string; rif: string; kind: 'customer' | 'distributor' }>();
      for (const c of customers.items) names.set(c.customerId, { name: c.name, rif: c.rif, kind: 'customer' });
      for (const d of distributors.items) names.set(d.distributorId, { name: d.name, rif: d.rif, kind: 'distributor' });
      return names;
    },
  });
}

/**
 * Exact inventory balances (admin/warehouse). Never customer-facing. Reads EVERY page: the
 * screen filters client-side and the home KPIs count rows, so a single 25-row page would
 * silently drop the rest.
 */
export function useInventory(query?: InventoryQuery) {
  return useQuery({
    queryKey: queryKeys.inventory(query),
    queryFn: () => fetchAllPages((cursor) => api.listInventory({ ...query, limit: ALL_PAGES_SIZE, cursor })),
  });
}

/**
 * Receive goods / correct a count (admin, warehouse). Refreshes the inventory lists and the
 * product catalog, whose availability labels are derived from these balances.
 */
export function useAdjustStock() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Inventario actualizado' },
    mutationFn: (input: AdjustStockInput) => api.adjustStock(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] });
      void qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/* --------------------------------- Orders --------------------------------- */

/**
 * Order list, scoped to the caller server-side. Pass `enabled: false` to defer
 * until a required filter (status/customerId) is chosen — admin/finance lists
 * are rejected by the backend without one.
 */
export function useOrders(query?: OrdersQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.orders(query),
    queryFn: () => api.listOrders(query),
    enabled,
  });
}

/** A single order with its items + reservations. */
export function useOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.order(orderId ?? ''),
    queryFn: () => api.getOrder(orderId as string),
    enabled: Boolean(orderId),
  });
}

/** Create an order; refreshes every order list (status filters vary) on success. */
export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Pedido creado' },
    mutationFn: (input: CreateOrderInput) => api.createOrder(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * Shared invalidation for order status transitions: refresh this order's detail
 * and every order list (a status change moves it between filter tabs).
 */
function useOrderTransition(
  orderId: string,
  run: (orderId: string, input: OrderDecisionInput) => Promise<unknown>,
  meta: Record<string, unknown> = { successMessage: 'Pedido actualizado' },
) {
  const qc = useQueryClient();
  return useMutation({
    meta,
    mutationFn: (input: OrderDecisionInput = {}) => run(orderId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.order(orderId) });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Submit a draft order (DRAFT → submitted | pending_approval). */
export function useSubmitOrder(orderId: string) {
  return useOrderTransition(orderId, (id) => api.submitOrder(id));
}

/** Approve a pending order (admin / sales_manager). Optional reason. */
export function useApproveOrder(orderId: string) {
  return useOrderTransition(orderId, (id, input) => api.approveOrder(id, input));
}

/** Reject a pending order (admin / sales_manager). Optional reason; frees stock. */
export function useRejectOrder(orderId: string) {
  return useOrderTransition(orderId, (id, input) => api.rejectOrder(id, input));
}

/** Cancel an order; releases any reservations. A refusal shows inside the confirm dialog. */
export function useCancelOrder(orderId: string) {
  return useOrderTransition(orderId, (id, input) => api.cancelOrder(id, input), {
    inlineError: true,
    successMessage: 'Pedido cancelado',
  });
}

/** Start fulfillment (submitted | approved → picking). Warehouse/admin. */
export function usePickOrder(orderId: string) {
  return useOrderTransition(orderId, (id, input) => api.pickOrder(id, input));
}

/** Dispatch the order — stock leaves the warehouse (→ dispatched). Warehouse/admin. */
export function useDispatchOrder(orderId: string) {
  return useOrderTransition(orderId, (id, input) => api.dispatchOrder(id, input));
}

/** Confirm delivery (dispatched → delivered). Warehouse/admin/driver. */
export function useDeliverOrder(orderId: string) {
  return useOrderTransition(orderId, (id, input) => api.deliverOrder(id, input));
}

/* ------------------------------- Cotizaciones ----------------------------- */

/**
 * Quote (cotización) list, scoped to the caller server-side (customer → own,
 * sales_rep → assigned, admin/finance → all). Pass `enabled: false` to defer
 * until a required filter (status/customerId) is chosen — admin/finance lists
 * are rejected by the backend without one.
 */
export function useQuotes(query?: QuotesQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.quotes(query),
    queryFn: () => api.listQuotes(query),
    enabled,
  });
}

/** A single quote (tenant-owned — no customerId needed, unlike invoices/payments). */
export function useQuote(quoteId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.quote(quoteId ?? ''),
    queryFn: () => api.getQuote(quoteId as string),
    enabled: Boolean(quoteId),
  });
}

/** Create a quote; refreshes every quote list (status filters vary) on success. */
export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Cotización creada' },
    mutationFn: (input: CreateQuoteInput) => api.createQuote(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

/**
 * Shared invalidation for quote status transitions (send/accept/reject): refresh
 * this quote's detail and every quote list (a status change moves it between
 * filter tabs). The mutation returns the updated `Quote`, so we also seed the
 * detail cache for an instant status flip.
 */
function useQuoteTransition(
  quoteId: string,
  run: (quoteId: string, input: QuoteDecisionInput) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Cotización actualizada' },
    mutationFn: (input: QuoteDecisionInput = {}) => run(quoteId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

/** Send a draft quote (DRAFT → sent). Optional reason. */
export function useSendQuote(quoteId: string) {
  return useQuoteTransition(quoteId, (id, input) => api.sendQuote(id, input));
}

/** Accept a sent quote (→ accepted). Optional reason. */
export function useAcceptQuote(quoteId: string) {
  return useQuoteTransition(quoteId, (id, input) => api.acceptQuote(id, input));
}

/** Reject a sent quote (→ rejected). Optional reason. */
export function useRejectQuote(quoteId: string) {
  return useQuoteTransition(quoteId, (id, input) => api.rejectQuote(id, input));
}

/**
 * Convert an accepted/sent quote into a real order. The order locks the supplied
 * exchange rate (VES per 1 USD) and reserves stock. On success the quote moves to
 * `converted`, so refresh this quote's detail + every quote list; the created
 * order shows up under the orders lists too, so refresh those as well.
 */
export function useConvertQuote(quoteId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Cotización convertida en pedido' },
    mutationFn: (input: ConvertQuoteInput) => api.convertQuote(quoteId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: ['quotes'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/* ---------------------------------- SKUs ---------------------------------- */

/** A product's sellable SKUs (code, unit, base price). */
export function useSkus(productId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.skus(productId ?? ''),
    queryFn: () => api.listSkus(productId as string),
    enabled: Boolean(productId),
  });
}

/** Create a SKU under a product (admin); refreshes that product's SKU list. */
export function useCreateSku(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'SKU creado' },
    mutationFn: (input: CreateSkuInput) => api.createSku(productId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skus(productId) });
    },
  });
}

/* --------------------------------- Pagos ---------------------------------- */

/**
 * Payments list, scoped to the caller server-side. Pass `status`/`accountId`
 * filters as needed. Used by both the Pagos list (tabs) and the finance review
 * queue (status = pending).
 */
export function usePayments(query?: PaymentsQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.payments(query),
    queryFn: () => api.listPayments(query),
    enabled,
  });
}

/** A single payment (amounts, method, proof, review state). */
export function usePayment(paymentId: string | undefined, accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.payment(paymentId ?? '', accountId ?? ''),
    queryFn: () => api.getPayment(paymentId as string, accountId as string),
    // The backend requires accountId to locate the payment's partition, so gate on both.
    enabled: Boolean(paymentId) && Boolean(accountId),
  });
}

/**
 * Register a payment for review; refreshes payment lists + customer balances.
 * The proof file is uploaded first (presign → PUT to S3); the resulting
 * `proofS3Key` is passed through here so the backend links it to the payment.
 */
export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Pago registrado' },
    mutationFn: (input: CreatePaymentInput & { proofS3Key?: string }) => api.createPayment(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      // Balances/credit may change once finance approves; refresh customers.
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/**
 * Shared invalidation for a payment review decision (approve/reject): refresh
 * this payment's detail, every payment list (the row moves between tabs), and
 * customer balances (an approval applies the amount).
 */
function usePaymentDecision(
  paymentId: string,
  run: (paymentId: string, input: PaymentDecisionInput) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Pago actualizado' },
    mutationFn: (input: PaymentDecisionInput = {}) => run(paymentId, input),
    onSuccess: () => {
      // Prefix match covers payment(id) and payment(id, accountId) detail keys.
      void qc.invalidateQueries({ queryKey: ['payments', 'detail', paymentId] });
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/** Approve a manual payment (finance/admin). `accountId` locates the payment's partition. */
export function useApprovePayment(paymentId: string, accountId: string) {
  return usePaymentDecision(paymentId, (id, input) => api.approvePayment(id, accountId, input));
}

/** Reject a manual payment (finance/admin). `accountId` locates the payment's partition. */
export function useRejectPayment(paymentId: string, accountId: string) {
  return usePaymentDecision(paymentId, (id, input) => api.rejectPayment(id, accountId, input));
}

/**
 * "Todos" for the list endpoints that REQUIRE a status filter (admin/finance: orders, quotes,
 * invoices). One query per status, merged newest first, so the tab needs no backend change.
 * Each status keeps its own cache entry under `[scope, 'by-status', status]`, so the existing
 * `invalidateQueries({ queryKey: [scope] })` after a change refreshes it too. Each status
 * returns at most one page (same cap as the single-status tabs).
 */
export function useAllStatusList<T, S extends string>({
  scope,
  statuses,
  fetchStatus,
  sortDate,
  enabled,
}: {
  scope: 'orders' | 'quotes' | 'invoices';
  statuses: readonly S[];
  fetchStatus: (status: S) => Promise<{ items: T[] }>;
  /** ISO date/time used to order the merged rows (newest first). */
  sortDate: (item: T) => string;
  enabled: boolean;
}) {
  return useQueries({
    queries: statuses.map((status) => ({
      queryKey: [scope, 'by-status', status] as const,
      queryFn: () => fetchStatus(status),
      enabled,
    })),
    combine: (results) => ({
      items: results
        .flatMap((r) => r.data?.items ?? [])
        .sort((a, b) => sortDate(b).localeCompare(sortDate(a))),
      isLoading: results.some((r) => r.isLoading),
      isError: results.some((r) => r.isError),
      error: results.find((r) => r.error)?.error ?? null,
      refetch: () => Promise.all(results.map((r) => r.refetch())),
    }),
  });
}

/* -------------------------------- Facturas -------------------------------- */

/**
 * Invoices list, scoped to the caller server-side (customer/distributor → own
 * account, finance/admin → all). For admin/finance the backend REQUIRES a
 * `customerId` OR `status` filter, so pass `enabled: false` until one is chosen.
 * Used by the Facturas list (tabs) and the payment-allocation panel (open invoices).
 */
export function useInvoices(query?: InvoicesQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.invoices(query),
    queryFn: () => api.listInvoices(query),
    enabled,
  });
}

/** A single invoice (kind, status, total/paid/balance, dates). */
export function useInvoice(invoiceId: string | undefined, customerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.invoice(invoiceId ?? '', customerId ?? ''),
    queryFn: () => api.getInvoice(invoiceId as string, customerId as string),
    // The backend requires customerId to locate the invoice's partition, so gate on both.
    enabled: Boolean(invoiceId) && Boolean(customerId),
  });
}

/**
 * Generate a billing document from an order (finance/admin); idempotent per
 * order. Refreshes every invoice list so the new invoice appears under its
 * status tab.
 */
export function useCreateInvoiceFromOrder() {
  const qc = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Factura generada' },
    mutationFn: (input: CreateInvoiceFromOrderInput) => api.createInvoiceFromOrder(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/** Void an invoice that has no payments applied (finance/admin); the reason is required. */
export function useVoidInvoice(invoiceId: string, customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Factura anulada' },
    mutationFn: (reason: string) => api.voidInvoice(invoiceId, { customerId, reason }),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.invoice(invoiceId, customerId), updated);
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

/**
 * Apply an approved payment across invoices (finance/admin). `accountId` locates
 * the payment's partition. Allocation changes invoice balances/status, so refresh
 * every invoice list + this payment's detail (the allocation links them).
 */
export function useAllocatePayment(paymentId: string, accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true },
    mutationFn: (input: AllocatePaymentInput) => api.allocatePayment(paymentId, accountId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      // Prefix match covers payment(id) and payment(id, accountId) detail keys.
      void qc.invalidateQueries({ queryKey: ['payments', 'detail', paymentId] });
    },
  });
}

/* ----------------------------- Notificaciones ----------------------------- */

/**
 * The caller's in-app notifications + total unread count. `enabled` gates it so
 * it never fires while signed out. Polls in the background so the bell badge
 * stays roughly current without a manual refresh.
 */
export function useNotifications(query?: NotificationsQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.notifications(query),
    queryFn: () => api.listNotifications(query),
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 60_000 : false,
  });
}

/**
 * Mark notifications read (specific ids or `all`). Refreshes every notifications
 * query so both the bell badge and the full page reflect the new read state.
 */
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true },
    mutationFn: (input: MarkNotificationsReadInput) => api.markNotificationsRead(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/* -------------------------------- Reportes -------------------------------- */

/**
 * Finance/admin Reportes dashboard snapshot (AR aging totals, pending payments,
 * open-orders-by-status, low-stock). Backend gates this to finance/admin (others
 * get 403); the admin route group is exactly those roles, so no `enabled` gate is
 * needed. A 60s staleTime keeps the panel cheap while still feeling live.
 */
export function useReportsDashboard() {
  return useQuery({
    queryKey: queryKeys.reportsDashboard,
    queryFn: () => api.getReportsDashboard(),
    staleTime: 60_000,
  });
}

/**
 * Finance/admin AR aging detail (open-balance total + buckets + per-invoice
 * items). Same authorization + staleTime rationale as {@link useReportsDashboard}.
 */
export function useArAging() {
  return useQuery({
    queryKey: queryKeys.arAging,
    queryFn: () => api.getArAging(),
    staleTime: 60_000,
  });
}

/* ------------------------------- Settings (exchange rate) ------------------------------- */

/** The active USD->VES rate (null when none set). Readable by any authenticated role. */
export function useExchangeRate() {
  return useQuery({
    queryKey: queryKeys.exchangeRate,
    queryFn: () => api.getExchangeRate(),
    staleTime: 60_000,
  });
}

/** Set the live rate (admin/finance). Refreshes the active rate + its history on success. */
export function useSetExchangeRate() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true },
    mutationFn: (input: SetExchangeRateInput) => api.setExchangeRate(input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.exchangeRate, updated);
      void qc.invalidateQueries({ queryKey: queryKeys.exchangeRateHistory });
    },
  });
}

/** Rate-change history, newest first (admin/finance). */
export function useExchangeRateHistory() {
  return useQuery({
    queryKey: queryKeys.exchangeRateHistory,
    queryFn: () => api.getExchangeRateHistory(),
    staleTime: 60_000,
  });
}

/* ------------------------------- Documents ------------------------------- */

/** An account's documents (newest first). Disabled until an accountId is known. */
export function useDocuments(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents(accountId ?? ''),
    queryFn: () => api.listDocuments(accountId as string),
    enabled: Boolean(accountId),
  });
}

/** Register a document after its file was uploaded to S3. Refreshes the account's list. */
export function useConfirmDocument(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true },
    mutationFn: (input: ConfirmDocumentInput) => api.confirmDocument(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.documents(accountId) });
    },
  });
}

/** Approve/reject a document (admin/finance). Refreshes the account's list. */
export function useReviewDocument(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true },
    mutationFn: ({ documentId, input }: { documentId: string; input: ReviewDocumentInput }) =>
      api.reviewDocument(accountId, documentId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.documents(accountId) });
    },
  });
}

/** The admin/finance review queue — documents in a given status across all accounts. */
export function useDocumentReviewQueue(status: string) {
  return useQuery({
    queryKey: queryKeys.documentReviewQueue(status),
    queryFn: () => api.getDocumentReviewQueue(status),
    staleTime: 30_000,
  });
}

/** Approve/reject from the review queue (the account id rides on each row). */
export function useReviewDocumentInQueue() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true },
    mutationFn: ({
      accountId,
      documentId,
      input,
    }: {
      accountId: string;
      documentId: string;
      input: ReviewDocumentInput;
    }) => api.reviewDocument(accountId, documentId, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['documents', 'review-queue'] });
      void qc.invalidateQueries({ queryKey: queryKeys.documents(vars.accountId) });
    },
  });
}

/* ------------------------------- Promotions ------------------------------- */

/** Promotions list (staff see all; customers/distributors see live only — gated server-side). */
export function usePromotions() {
  return useQuery({
    queryKey: queryKeys.promotions,
    queryFn: () => api.listPromotions(),
    staleTime: 30_000,
  });
}

/** A single promotion. */
export function usePromotion(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.promotion(id ?? ''),
    queryFn: () => api.getPromotion(id as string),
    enabled: Boolean(id),
  });
}

/** Create a promotion (admin). Refreshes the list on success. */
export function useCreatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Promoción creada' },
    mutationFn: (input: CreatePromotionInput) => api.createPromotion(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.promotions });
    },
  });
}

/** Update a promotion (admin), e.g. toggle active. Refreshes the list + that promotion. */
export function useUpdatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    meta: { inlineError: true, successMessage: 'Promoción actualizada' },
    mutationFn: ({ id, input }: { id: string; input: UpdatePromotionInput }) =>
      api.updatePromotion(id, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.promotion(updated.promotionId), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.promotions });
    },
  });
}
